import { useState } from "react";
import { Download, FileText } from "lucide-react";
import { useTranslation } from "../../../context/TranslationContext";
import { useAuth } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";
import { ModalWrapper, ModalHeader, ModalBody, ModalFooter, InfoBox, FormField, FormSelect, Button } from "../../common";
import toast from "react-hot-toast";
import { loadExcelJS } from "../../../utils/lazyImports";
import { getAllAvailabilityReport } from "../../../lib/api/report";

interface AvailabilityReportModalProps {
  onClose: () => void;
}

interface DailyAvailability {
  am: boolean;
  pm: boolean;
}

interface UserAvailabilityReport {
  initials: any;
  userId: string;
  firstName?: string;
  lastName?: string;
  availability: {
    [date: string]: DailyAvailability;
  };
  error?: string;
}

export function AvailabilityReportModal({ onClose }: AvailabilityReportModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { currentUser } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear().toString());

  const exportToExcel = async () => {
    if (!token || !currentUser) {
      toast.error(t("auth.authenticationRequired"));
      return;
    }

    setIsLoading(true);
    try {
      // Dynamically load ExcelJS only when needed
      const ExcelJS = await loadExcelJS();
      
      const data = await getAllAvailabilityReport(token, currentUser.site, year);

      if (!data || data.length === 0) {
        toast.error(t("reports.noDataAvailable"));
        setIsLoading(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Availability");

      // Build headers: userId, Date, Period
      const headers = ["User", t("common.date"), "Period", "Value"];
      worksheet.addRow(headers);

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE6E6E6' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });

      // Process each user's availability
      data.forEach((userReport: UserAvailabilityReport) => {

        // Sort dates for consistent output
        const sortedDates = Object.keys(userReport.availability).sort();

        sortedDates.forEach((date) => {
          const dayAvailability = userReport.availability[date];

          // Add row for AM if available
          // if (!dayAvailability.am) {
            worksheet.addRow([
              userReport.initials,
              date,
              "am",
              dayAvailability.am ? "Available" : "Unavailable"
            ]);
          // }

          // Add row for PM if available
          // if (!dayAvailability.pm) {
            worksheet.addRow([
              userReport.initials,
              date,
              "pm",
              dayAvailability.pm ? "Available" : "Unavailable"
            ]);
          // }
        });
      });

      // Set column widths
      worksheet.getColumn(1).width = 20; // User ID
      worksheet.getColumn(2).width = 15; // Date
      worksheet.getColumn(3).width = 10; // Period

      // Add borders to all cells
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          });
        }
      });

      const fileName = `Availability_Report_${year}_${new Date().toISOString().split('T')[0]}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(t("reports.exportSuccess"));
    } catch (error) {
      console.error("Failed to export availability:", error);
      toast.error(t("reports.exportFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear + i - 2).toString());

  return (
    <ModalWrapper onClose={onClose} size="md">
      <ModalHeader
        icon={<FileText />}
        title={t("reports.availabilityReport")}
        onClose={onClose}
      />

      <ModalBody>
        <InfoBox variant="info" icon={<FileText />} title={t("reports.availabilityReportDescription")}>
          {t("reports.availabilityReportInstructions")}
        </InfoBox>

        <FormField label={t("common.year")}>
          <FormSelect
            value={year}
            onChange={(e) => setYear(e.target.value)}
            options={years.map((y) => ({ value: y, label: y }))}
          />
        </FormField>
      </ModalBody>

      <ModalFooter alignment="right">
        <Button
          variant="secondary"
          onClick={onClose}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={exportToExcel}
          disabled={isLoading}
          loading={isLoading}
          icon={<Download />}
        >
          {t("reports.exportToExcel")}
        </Button>
      </ModalFooter>
    </ModalWrapper>
  );
}
